import { useEffect, useState } from "react";
import axiosInstance from "../../api/axiosInstance";
import { ChevronDown, RefreshCw } from "lucide-react";
import RangeInputs from "../../components/RangeInputs";

const CallLogBoard = () => {
  const [statuses, setStatuses] = useState([]);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    date: [null, null],
    call_status: "",
  });

  const [loadingRetranscripts, setLoadingRetranscripts] = useState([]);

  const fetchStatuses = async () => {
    try {
      const resp = await axiosInstance.get(`${process.env.REACT_APP_BASE_URL}api/webhooks/call-statuses`);
      setStatuses(resp.data.data || []);
    } catch (err) {
      console.error("Failed to fetch call statuses", err);
    }
  };

  const fetchCalls = async (filters) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      if (filters.name) params.append("name", filters.name);
      if (filters.call_status) params.append("call_status", filters.call_status);
      if (filters.date?.[0]) params.append("date_from", filters.date[0]);
      if (filters.date?.[1]) params.append("date_to", filters.date[1]);

      const resp = await axiosInstance.get(`${process.env.REACT_APP_BASE_URL}api/webhooks/calls?limit=100000&${params.toString()}`);

      const { data } = resp.data;
      if (Array.isArray(data)) {
        const sorted = data.sort((a, b) => new Date(b.call_datetime) - new Date(a.call_datetime));
        setCalls(sorted);
      }
    } catch (err) {
      console.error("Failed to fetch calls", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const name = params.get("name") || "";
    const call_status = params.get("call_status") || "";
    const date_from = params.get("date_from");
    const date_to = params.get("date_to");

    // 🕒 Calculate yesterday’s start and end in YYYY-MM-DD format
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    const yesterdayStart = new Date(yesterday);
    yesterdayStart.setHours(0, 0, 0, 0);

    const yesterdayEnd = new Date(yesterday);
    yesterdayEnd.setHours(23, 59, 59, 999);

    const formatDate = (d) => d.toISOString().split("T")[0];

    const initialFormData = {
      name,
      call_status,
      date: [date_from || formatDate(yesterdayStart), date_to || formatDate(yesterdayEnd)],
    };

    setFormData(initialFormData);
    fetchCalls(initialFormData);
  }, []);

  useEffect(() => {
    fetchStatuses();
  }, []);

  const updateUrlParams = (data) => {
    const params = new URLSearchParams();

    if (data.name) params.set("name", data.name);
    if (data.call_status) params.set("call_status", data.call_status);
    if (data.date?.[0]) params.set("date_from", data.date[0]);
    if (data.date?.[1]) params.set("date_to", data.date[1]);

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState(null, "", newUrl);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      updateUrlParams(updated);
      return updated;
    });
  };

  const handleFilter = (e) => {
    e.preventDefault();
    fetchCalls(formData);
  };

  const formatDate = (dt) =>
    new Intl.DateTimeFormat("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(dt));

  const grouped = calls.reduce((acc, call) => {
    const status = call.call_status || "Unknown";
    if (!acc[status]) acc[status] = {};

    const key = call.contact_name || "Unknown Contact";
    if (!acc[status][key]) acc[status][key] = [];

    acc[status][key].push(call);
    return acc;
  }, {});

  const handleRetranscript = async (id, recordingUrl) => {
    setLoadingRetranscripts((prev) => [...prev, id]);

    try {
      const response = await axiosInstance.put(`api/dealmachine_properties/call-logs-board`, {
        id,
        recordingurl: recordingUrl,
      });

      if (response.status === 200 && response.data) {
        const { call_transcript, call_analyzed_transcript } = response.data;

        setCalls((prevCalls) => prevCalls.map((call) => (call.id === id ? { ...call, call_transcript, call_analyzed_transcript } : call)));
      }
    } catch (error) {
      console.error("Retranscript error:", error);
    } finally {
      setTimeout(() => {
        setLoadingRetranscripts((prev) => prev.filter((item) => item !== id));
      }, 3000);
    }
  };

  return (
    <div
      className="bg-cover bg-center bg-no-repeat bg-fixed !pb-2 min-h-[92vh] -mt-6 pt-6"
      style={{
        backgroundImage: `url("https://images.pexels.com/photos/884788/pexels-photo-884788.jpeg")`,
      }}
    >
      <div className="!pt-4">
        <form onSubmit={handleFilter} className="input_fields_container SP_search_container !grid md:!grid-cols-3 gap-2 sm:gap-4 md:gap-6">
          <div className="relative w-full flex item-center">
            <input value={formData.name} onChange={(e) => handleChange("name", e.target.value)} placeholder="John Doe" type="text" className="input_field SP_input_field focus:!outline-none w-full pr-8" />
          </div>
          <RangeInputs type="date" label="Date" value={formData.date} onChange={(val) => handleChange("date", val)} disableFuture />
          <div className="relative w-full flex item-center">
            <select name="call_status" className="input_field SP_input_field focus:!outline-none w-full !pr-8 bg-white cursor-pointer" value={formData.call_status || ""} onChange={(e) => handleChange("call_status", e.target.value)}>
              <option value="">Select Call Status</option>
              {statuses.map((st) => (
                <option key={st} value={st}>
                  {st}
                </option>
              ))}
            </select>
          </div>
          <div></div>
          <div className="md:col-span-3 flex justify-center item-center">
            <button className="input_button" type="submit" disabled={loading}>
              {loading ? "Finding Calls..." : "Find Calls"}
            </button>
          </div>
        </form>
      </div>
      <div className="w-full overflow-x-auto call-logs-scroll-x !p-6">
        {!loading && calls.length === 0 && <p className="text-gray-500 text-center mt-10">No call logs found.</p>}

        {loading && (
          <div className="flex gap-4 animate-pulse !pb-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="w-82 min-w-82 h-[92vh] bg-gray-200 rounded-xl"></div>
            ))}
          </div>
        )}

        {!loading && calls.length > 0 && (
          <div className="flex gap-5 max-w-36">
            {Object.keys(grouped).map((callStatus, sIndex) => {
              const statusCount = Object.values(grouped[callStatus] || {}).reduce((sum, arr) => sum + (arr?.length || 0), 0);

              return (
                <div key={callStatus} className="w-82 h-min max-h-[92vh] min-w-[18rem] !px-1 !py-2 rounded-xl shadow transform transition-all duration-700 ease-out translate-x-0 opacity-100 bg-gray-200 text-gray-700" style={{ transitionDelay: `${sIndex * 120}ms` }}>
                  <div className="flex justify-between item-center !px-4 !mb-2">
                    <h2 className="text-left font-semibold">{callStatus}</h2>
                    <span className="text-sm font-medium">{statusCount}</span>
                  </div>

                  <div className="flex flex-col gap-4 overflow-y-auto max-h-[82vh] !pr-1 !pb-2 !mb-4 call-logs-scroll-y">
                    {Object.keys(grouped[callStatus]).map((contact, cIndex) => {
                      const firstCall = grouped[callStatus][contact][0];
                      const receiverName = firstCall?.caller?.fname || firstCall?.caller?.lname ? `${firstCall.caller.fname || ""} ${firstCall.caller.lname || ""}`.trim() : firstCall?.caller?.calleridName || "Unknown Caller";

                      return (
                        <div key={contact} className="bg-gray-50 !p-2 rounded-lg shadow-sm transform transition-all duration-500 translate-y-0 opacity-100" style={{ transitionDelay: `${cIndex * 80}ms` }}>
                          {/* Receiver name and contact shown once */}
                          <div className="flex justify-between items-center text-gray-600 !pb-1 !mb-2">
                            <span className="text-sm font-semibold">{receiverName}</span>
                            <span className="text-sm font-medium text-gray-500">{contact}</span>
                          </div>

                          {/* Each call entry */}
                          {grouped[callStatus][contact].map((call, i) => {
                            // const callerName = call?.caller?.calleridName || (call?.caller?.fname || call?.caller?.lname ? `${call.caller.fname || ""} ${call.caller.lname || ""}`.trim() : "Unknown Caller");

                            return (
                              <div key={call.id} className="!mb-2 border-b border-gray-200 !pb-2 last:border-0">
                                {/* <p className="text-xs font-semibold text-gray-700 !mb-1">Caller: {callerName}</p> */}
                                <p className="text-xs text-gray-500 !mb-2">{formatDate(call.call_datetime)}</p>

                                {call.call_recording ? (
                                  <div className="flex item-center !space-x-2">
                                    <div className="audio-wrapper w-full">
                                      <audio controls src={call.call_recording} className="w-full" />
                                    </div>
                                    {call.call_recording && !call.call_transcript && (
                                      <div className="flex justify-center item-center">
                                        <RefreshCw size={15} onClick={() => handleRetranscript(call.id, call.call_recording)} className={`!mt-1.5 cursor-pointer duration-200 ease-in-out ${loadingRetranscripts.includes(call.id) ? "animate-spin text-green-400" : "hover:text-green-400 text-gray-500"}`} />
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <p className="text-xs text-gray-400 italic">No recording</p>
                                )}

                                {call.call_analyzed_transcript ? (
                                  <div className="bg-gray-200 rounded text-sm text-gray-700">
                                    <div onClick={() => setCalls((prev) => prev.map((c) => (c.id === call.id ? { ...c, _open: !c._open } : c)))} className="cursor-pointer !px-2 !py-1 font-medium flex items-center justify-between !select-none">
                                      <span>Transcript</span>
                                      <ChevronDown className={`!h-4 !w-4 transition-transform duration-300 ${call._open ? "rotate-180" : ""}`} />
                                    </div>
                                    <div className="!pr-2">
                                      <div className={`overflow-y-auto call-logs-scroll-y text-[8pt] transition-[max-height,opacity] duration-300 ease-in-out ${call._open ? "max-h-40 opacity-100" : "max-h-0 opacity-0"}`}>
                                        <div className="!p-2 border-t border-gray-200 whitespace-pre-line text-left">{call.call_analyzed_transcript}</div>
                                      </div>
                                    </div>
                                  </div>
                                ) : call.call_transcript ? (
                                  <div className="bg-gray-200/70 rounded text-sm text-gray-700">
                                    <div onClick={() => setCalls((prev) => prev.map((c) => (c.id === call.id ? { ...c, _open: !c._open } : c)))} className="cursor-pointer !px-2 !py-1 font-medium flex items-center justify-between !select-none">
                                      <span>Transcript</span>
                                      <ChevronDown className={`!h-4 !w-4 transition-transform duration-300 ${call._open ? "rotate-180" : ""}`} />
                                    </div>
                                    <div className="!pr-2">
                                      <div className={`overflow-y-auto call-logs-scroll-y text-[8pt] transition-[max-height,opacity] duration-300 ease-in-out ${call._open ? "max-h-40 opacity-100" : "max-h-0 opacity-0"}`}>
                                        <div className="!p-2 border-t border-gray-200 whitespace-pre-line text-left">{call.call_transcript}</div>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <p className="text-[11px] text-gray-400 italic">No transcript</p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CallLogBoard;
